import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
} from "@components/common/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DictType } from "@pma/types";
import { Input } from "@components/common/input";
import { Button } from "@components/common/button";
import axios from "axios";
import { useForm } from "react-hook-form";
import { ADD_WMS_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes";
import { toast } from "@hooks/use-toast";
import { cn, isValidUrl } from "@pma/utils/helpers";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { parseCookies } from "nookies";
import { useAuth } from "@stores/auth-store";
import { useEffect, useRef, useState } from "react";
import { parseString } from "xml2js";
import useTranslator from "@hooks/use-translator";

interface GisEndpointFormProps extends React.HTMLAttributes<HTMLDivElement> {
  refetch: CallableFunction;
}

const formSchema = z
  .object({
    name: z.string().min(1, { message: "Please a name" }),
    url: z.string().min(1, {
      message: "Please a valid url",
    }),
  })
  .refine((data: DictType) => isValidUrl(data?.url), {
    message: "Please enter a valid url",
    path: ["url"],
  });

export default function AddGisEndpoint({
  refetch,
  className,
  ...props
}: GisEndpointFormProps) {
  const [endpointMeta, setEndpointMeta] = useState<DictType>({
    name: "",
    url: "",
  });
  const [fetchLayers, setFetchLayers] = useState(false);
  const { translate } = useTranslator();

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const { authConfig } = useAuth();

  const orgId = authConfig?.last_organization_id;

  const gisModalRef = useRef<HTMLButtonElement | null>(null);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const getLayers = async () => {
    const response = await axios.get(endpointMeta.url);

    return response.data;
  };

  const { data, isFetched } = useQuery({
    queryKey: ["gis-layers"],
    queryFn: getLayers,
    enabled: fetchLayers,
  });

  const addGisEndpoint = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + ADD_WMS_ENDPOINT,
      {
        wms: JSON.stringify(payload),
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const addGisEndpointMutation = useMutation({
    mutationFn: addGisEndpoint,
    mutationKey: ["gis-endpoint"],
    onSuccess: (data) => {
      setFetchLayers(false);
      setEndpointMeta({});
      refetch();
      gisModalRef?.current?.click();
      toast({
        title: translate("Success"),
        description: translate("Gis point added successfully"),
        variant: "success",
      });
    },
    onError: (error: DictType) => {
      setFetchLayers(false);
      toast({
        title: translate("Error"),
        description: translate("Unable to add gis endpoint"),
        variant: "error",
      });
    },
  });

  function onSubmit(e: any) {
    e?.preventDefault();
    if (isValidUrl(endpointMeta?.url)) {
      setFetchLayers(true);
    } else {
      toast({
        title: translate("Info"),
        description: translate("Enter a valid url"),
        variant: "info",
      });
    }
  }

  useEffect(() => {
    if (isFetched && endpointMeta?.url) {
      parseString(data, (err, result) => {
        if (err) {
          toast({
            title: translate("Error"),
            description: `${translate("Error parsing XML :")}${err}`,
            variant: "error",
          });
        } else {
          const featureTypes =
            result["wfs:WFS_Capabilities"]["wfs:FeatureTypeList"][0][
              "wfs:FeatureType"
            ];
          const layers: any = [];

          featureTypes.forEach((featureType: any) => {
            const layerName = featureType["wfs:Title"][0];
            layers.push({
              name: layerName,
              geometryType: "",
            });
          });
          const payload: any = {
            name: endpointMeta.name,
            type: "wfs",
            url: endpointMeta?.url,
            layers,
          };
          addGisEndpointMutation.mutate(payload);
        }
      });
    }
  }, [isFetched]);

  return (
    <Dialog>
      <DialogTrigger>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 rounded-full bg-background"
        >
          {translate("Add Endpoint")}{" "}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex flex-col items-center justify-center gap-y-4 pb-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {translate("Add Endpoint")}{" "}
            </h1>
          </DialogTitle>
          <DialogDescription className="space-y-2 pt-2 text-center font-medium text-zinc-900 dark:text-white-fade">
            <div
              className={cn("grid gap-6 text-muted-foreground", className)}
              {...props}
            >
              <Form {...form}>
                <form onSubmit={onSubmit}>
                  <div className="grid gap-2">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="flex flex-col items-start justify-start space-y-2">
                          <FormLabel className="capitalize">
                            {translate("Name")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={translate("Endpoint")}
                              className="rounded-full text-black"
                              required
                              onChange={(e) =>
                                setEndpointMeta((prev) => ({
                                  ...prev,
                                  name: e.target.value,
                                }))
                              }
                              autoFocus
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="url"
                      render={({ field }) => (
                        <FormItem className="flex flex-col items-start justify-start space-y-2">
                          <FormLabel className="capitalize">
                            {translate("Url")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="https://..."
                              className="rounded-full text-black"
                              required
                              onChange={(e) =>
                                setEndpointMeta((prev) => ({
                                  ...prev,
                                  url: e.target.value,
                                }))
                              }
                              autoComplete="off"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      className="mt-2"
                      type="submit"
                      loading={addGisEndpointMutation.isPending}
                      disabled={addGisEndpointMutation.isPending}
                    >
                      {translate("Add endpoint")}
                    </Button>
                    <DialogClose ref={gisModalRef}></DialogClose>
                  </div>
                </form>
              </Form>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
